import React, { useMemo, useState } from 'react';
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { Form, Row, Col, Card, Upload, Button, message } from 'antd';
import { editPreClearance, uploadDocument } from '@app/api/pre-clearance.api';

interface SubmittedDoc {
  name: string;
  document_id: string;
  status?: string;
  file?: string;
  image_url?: string;
  document_url?: string;
}

interface DocumentChecklistProps {
  editable: boolean;
  initialformFields: SubmittedDoc[];
  formFields: {
    name: string;
    document_id: string;
  }[];
  status: string;
  pre_clearance_id: string | null;
}

interface DocItem {
  document_id: string;
  name: string;
  file?: string;
  url?: string;
}

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_FORMATS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const UPLOAD_STATUSES = ['PENDING_DOCUMENT', 'PENDING_REVIEW'];

const resolveUrl = (doc: { document_url?: string; image_url?: string }) =>
  doc?.document_url || doc?.image_url || undefined;

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  editable,
  initialformFields = [],
  formFields = [],
  status,
  pre_clearance_id,
}) => {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Docs uploaded in this session — kept separate so a parent re-render can't wipe them
  const [uploaded, setUploaded] = useState<Record<string, DocItem>>({});

  const statusAllowsUpload = UPLOAD_STATUSES.includes(status);
  const canUpload = editable && statusAllowsUpload;

  // Slots (formFields) → overlay submitted docs (initialformFields) → overlay session uploads
  const documents = useMemo<DocItem[]>(() => {
    const map = new Map<string, DocItem>();

    formFields.forEach((slot) => {
      map.set(slot.document_id, { document_id: slot.document_id, name: slot.name });
    });

    initialformFields.forEach((doc) => {
      const existing = map.get(doc.document_id);
      map.set(doc.document_id, {
        document_id: doc.document_id,
        name: existing?.name ?? doc.name,
        file: doc.file,
        url: resolveUrl(doc),
      });
    });

    Object.values(uploaded).forEach((doc) => {
      map.set(doc.document_id, { ...map.get(doc.document_id), ...doc });
    });

    return Array.from(map.values());
  }, [formFields, initialformFields, uploaded]);

  const setError = (documentId: string, msg: string) => {
    setErrors((prev) => ({ ...prev, [documentId]: msg }));
    message.error(msg);
  };

  const clearError = (documentId: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[documentId];
      return updated;
    });
  };

  const validateFile = (file: File, documentId: string): boolean => {
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setError(documentId, `Invalid file type. Only ${ACCEPTED_FORMATS.join(', ')} files are allowed.`);
      return false;
    }
    if (file.size === 0) {
      setError(documentId, 'The selected file is empty. Please choose a valid file.');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setError(documentId, `File size (${fileSizeMB}MB) exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
      return false;
    }
    return true;
  };

  const handleUpload = async (doc: DocItem, file: File) => {
    const { document_id: documentId } = doc;

    if (!canUpload) return;

    if (!pre_clearance_id || isNaN(Number(pre_clearance_id))) {
      setError(documentId, 'Pre Clearance ID is required.');
      return;
    }

    clearError(documentId);
    if (!validateFile(file, documentId)) return;

    setUploading((prev) => ({ ...prev, [documentId]: true }));

    try {
      let preClearanceData;
      try {
        preClearanceData = await editPreClearance(Number(pre_clearance_id));
      } catch {
        throw new Error('Failed to fetch pre-clearance data. Please try again.');
      }

      const userId = preClearanceData?.pre_clearance?.customer?.user?.id;
      if (!userId) {
        throw new Error('Failed to retrieve User ID. Please contact support.');
      }

      const formData = new FormData();
      formData.append('pre_clearance_id', pre_clearance_id);
      formData.append('user_id', String(userId));
      formData.append('document_id', documentId);
      formData.append('name', doc.name ?? '');
      formData.append('file', file, file.name);
      formData.append('verified', 'false');

      let response: any;
      try {
        response = await uploadDocument(Number(pre_clearance_id), formData);
      } catch (uploadError: any) {
        const httpStatus = uploadError?.response?.status;
        if (httpStatus === 413) throw new Error('File is too large for the server. Please upload a smaller file.');
        if (httpStatus === 415) throw new Error('File type not supported by the server.');
        if (httpStatus === 401 || httpStatus === 403) throw new Error('You are not authorised to upload documents.');
        if (httpStatus >= 500) throw new Error('Server error occurred. Please try again later.');
        throw new Error('Upload failed. Please check your connection and try again.');
      }

      const uploadedDocs = response?.data?.pc_documents ?? response?.data?.pre_clearance_documents;
      if (!Array.isArray(uploadedDocs)) {
        throw new Error('Upload response was invalid. Please try again.');
      }

      const uploadedDoc = uploadedDocs.find((d: any) => d.document_id?.trim() === documentId.trim());

      setUploaded((prev) => ({
        ...prev,
        [documentId]: {
          document_id: documentId,
          name: doc.name,
          file: uploadedDoc?.file ?? file.name,
          url: uploadedDoc?.document_url || URL.createObjectURL(file),
        },
      }));

      message.success(response?.message ?? `${file.name} uploaded successfully.`);
    } catch (error: any) {
      setError(documentId, error?.message ?? `Failed to upload ${file.name}. Please try again.`);
      console.error('Upload error:', error);
    } finally {
      setUploading((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const renderCard = (doc: DocItem) => {
    const isUploading = !!uploading[doc.document_id];
    const hasFile = !!doc.url;
    const fileName = doc.file || doc.name;

    return (
      <Col xs={24} sm={12} key={doc.document_id}>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200" style={{ minHeight: 160 }}>
          <div className="flex flex-col gap-3">
            <span className="text-gray-700 font-medium text-sm">{doc.name}</span>

            <div
              className={`bg-gray-50 p-4 rounded-lg border ${
                errors[doc.document_id] ? 'border-red-400' : 'border-gray-200'
              } flex flex-col gap-3`}
              style={{ minHeight: 90 }}
            >
              {statusAllowsUpload && (
                <Upload
                  accept={ACCEPTED_FORMATS.join(',')}
                  beforeUpload={(file) => {
                    handleUpload(doc, file);
                    return false;
                  }}
                  showUploadList={false}
                  disabled={!canUpload || isUploading}
                >
                  <Button size="small" disabled={!canUpload || isUploading} style={{ marginTop: '10px' }}>
                    <UploadOutlined className="text-gray-500" />
                    <span>{isUploading ? 'Uploading...' : hasFile ? 'Re-Upload' : 'Upload'}</span>
                  </Button>
                </Upload>
              )}

              {hasFile ? (
                <div className="flex flex-col gap-2" style={{ marginTop: '10px' }}>
                  <div className="flex items-center gap-1 min-w-0">
                    <PaperClipOutlined className="text-gray-400 flex-shrink-0 text-xs" />
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 text-xs truncate hover:underline"
                      title={fileName}
                    >
                      {fileName}
                    </a>
                  </div>
                  <div className="text-green-500 text-sm">✓ File uploaded</div>
                </div>
              ) : (
                !statusAllowsUpload && <span className="text-gray-400 text-xs">Not uploaded</span>
              )}
            </div>

            {errors[doc.document_id] && <span className="text-red-500 text-xs">{errors[doc.document_id]}</span>}
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <div className="p-6 bg-gray-50 rounded-lg">
      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          {documents.length > 0 ? (
            documents.map(renderCard)
          ) : (
            <Col span={24}>
              <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">No documents available.</div>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default DocumentChecklist;
